
import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Search, Share2, FileText, Users, Mail, Link, IndianRupee } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

// Updated lender recommendation data
const lenderRecommendations = [
  {
    id: 1,
    name: "Capri Global",
    description: "Leading NBFC specializing in MSME loans and housing finance with flexible terms.",
    matchScore: 94,
    loanTypes: ["MSME Loans", "Housing Finance"],
    typicalAmount: "₹25 Cr - ₹200 Cr",
    typicalTenor: "3-7 years"
  },
  {
    id: 2,
    name: "HomeCredit",
    description: "Consumer finance provider offering quick financing solutions with minimal documentation.",
    matchScore: 87,
    loanTypes: ["Consumer Finance", "Working Capital"],
    typicalAmount: "₹10 Cr - ₹100 Cr",
    typicalTenor: "1-3 years"
  },
  {
    id: 3,
    name: "InCred",
    description: "Tech-focused NBFC providing innovative financing solutions for emerging businesses.",
    matchScore: 82,
    loanTypes: ["Business Loans", "Supply Chain Financing", "Personal Loans"],
    typicalAmount: "₹50 Cr - ₹300 Cr",
    typicalTenor: "2-5 years"
  }
];

const ALMBorrowings = () => {
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMethod, setShareMethod] = useState("link");
  const [emails, setEmails] = useState("");
  
  const handleShareDataRoom = () => {
    if (shareMethod === "link") {
      toast.success("Data room link generated and copied to clipboard!");
    } else {
      toast.success(`Data room access granted to ${emails.split(',').length} recipients`);
    }
    setShareDialogOpen(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Fresh Borrowings</h1>
        <p className="text-muted-foreground">
          Discover lenders and manage your data room for debt raise
        </p>
      </div>

      <Tabs defaultValue="lender-discovery" className="w-full">
        <TabsList>
          <TabsTrigger value="lender-discovery">Lender Discovery</TabsTrigger>
          <TabsTrigger value="data-room">Data Room</TabsTrigger>
        </TabsList>
        
        <TabsContent value="lender-discovery" className="space-y-4 py-4">
          <Card>
            <CardHeader>
              <CardTitle>Find Suitable Lenders</CardTitle>
              <CardDescription>
                Discover potential lenders who match your requirements or hire Rench's syndication desk
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input 
                      type="text"
                      placeholder="Search by loan type, amount, tenor..." 
                      className="pl-10 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                  <Button>Filter</Button>
                </div>
                
                <div>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Rench Syndication Desk</CardTitle>
                      <CardDescription>
                        Let our experts handle the debt raise process
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-4">
                        Our team will identify the right lenders, negotiate terms, and manage documentation
                        to optimize your debt raise.
                      </p>
                      <Button>Hire Syndication Desk</Button>
                    </CardContent>
                  </Card>
                </div>
                
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Lender Recommendations</CardTitle>
                    <CardDescription>Based on your borrowing profile and requirements</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {lenderRecommendations.map((lender) => (
                        <Card key={lender.id} className="p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-medium text-lg">{lender.name}</h4>
                              <p className="text-sm text-muted-foreground mb-1">{lender.description}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {lender.loanTypes.map((type, idx) => (
                                  <span key={idx} className="text-xs bg-muted px-2 py-0.5 rounded-full">
                                    {type}
                                  </span>
                                ))}
                              </div>
                              <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Typical Amount:</span>
                                  <p className="flex items-center gap-1">
                                    <IndianRupee className="h-3 w-3" />
                                    {lender.typicalAmount}
                                  </p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Typical Tenor:</span>
                                  <p>{lender.typicalTenor}</p>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col items-end">
                              <div className="bg-primary/10 text-primary font-medium rounded-full px-3 py-1 text-sm mb-3">
                                {lender.matchScore}% Match
                              </div>
                              <Button variant="outline" size="sm">Contact</Button>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="data-room" className="space-y-4 py-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Data Room</CardTitle>
                  <CardDescription>
                    Manage documents and share them with potential lenders
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline">
                    <FileText className="h-4 w-4 mr-2" />
                    Add Document
                  </Button>
                  <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Share2 className="h-4 w-4 mr-2" />
                        Share Data Room
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Share Data Room</DialogTitle>
                        <DialogDescription>
                          Choose how you want to share access to your data room
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="py-4">
                        <RadioGroup value={shareMethod} onValueChange={setShareMethod} className="space-y-4">
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value="link" id="link" />
                            <div className="grid gap-1.5">
                              <Label htmlFor="link" className="font-medium">Generate link</Label>
                              <p className="text-sm text-muted-foreground">
                                Create a link that anyone can use to access your data room
                              </p>
                              {shareMethod === "link" && (
                                <div className="flex mt-2">
                                  <Input 
                                    value="https://rench.finance/data-room/68d42f..." 
                                    readOnly 
                                    className="rounded-r-none" 
                                  />
                                  <Button variant="secondary" size="sm" className="rounded-l-none">
                                    <Link className="h-4 w-4 mr-1" />
                                    Copy
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-start space-x-3">
                            <RadioGroupItem value="email" id="email" />
                            <div className="grid gap-1.5">
                              <Label htmlFor="email" className="font-medium">Email-based access</Label>
                              <p className="text-sm text-muted-foreground">
                                Specific people can access with their email address
                              </p>
                              {shareMethod === "email" && (
                                <div className="mt-2">
                                  <Label htmlFor="emails" className="text-sm">
                                    Enter email addresses (separated by commas)
                                  </Label>
                                  <div className="flex mt-1">
                                    <Input
                                      id="emails"
                                      placeholder="example@company.com"
                                      value={emails}
                                      onChange={(e) => setEmails(e.target.value)}
                                      className="rounded-r-none" 
                                    />
                                    <Button variant="secondary" size="sm" className="rounded-l-none">
                                      <Mail className="h-4 w-4 mr-1" />
                                      Add
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </RadioGroup>
                      </div>
                      
                      <Separator />
                      
                      <DialogFooter className="sm:justify-between">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <Users className="h-4 w-4 mr-2" />
                          Currently shared with 0 people
                        </div>
                        <Button onClick={handleShareDataRoom}>Share</Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center mb-6">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search documents..." 
                      className="pl-10 w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                
                <div className="grid gap-4">
                  <Card className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <h4 className="font-medium">Financial Statements</h4>
                          <p className="text-xs text-muted-foreground">Auto-generated from your data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost">Preview</Button>
                        <Button size="sm" variant="outline">
                          <Share2 className="h-3 w-3 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <h4 className="font-medium">Debt Service Coverage Report</h4>
                          <p className="text-xs text-muted-foreground">Auto-generated from your data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost">Preview</Button>
                        <Button size="sm" variant="outline">
                          <Share2 className="h-3 w-3 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  </Card>
                  
                  <Card className="p-4">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-muted-foreground" />
                        <div>
                          <h4 className="font-medium">Cash Flow Projections</h4>
                          <p className="text-xs text-muted-foreground">Auto-generated from your data</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost">Preview</Button>
                        <Button size="sm" variant="outline">
                          <Share2 className="h-3 w-3 mr-1" />
                          Share
                        </Button>
                      </div>
                    </div>
                  </Card>
                </div>
                
                <div>
                  <h3 className="font-medium mb-2">Shared With</h3>
                  <div className="text-sm text-muted-foreground flex items-center">
                    <Users className="h-4 w-4 mr-2" />
                    No lenders have been given access to your data room yet.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ALMBorrowings;
